import { takeLatest, put, call } from 'redux-saga/effects';
import {
	makePostRequest,
	makeGetRequest,
	makePutRequest,
	makePostRequestWithFormData,
	makeGetRequestWithQueryParams,
	makePutRequestWithQueryParams,
	makeDeleteRequestWithParams
} from '../services/axios';
import { clientsApi, tokenAPI, systemContextsApi, sessionUserApi } from '../services/services';
import { loadAndAlertMsg } from '../utilities/utilities';
import { clientMsgs } from '../utilities/static-data';
import ClientActions from './client-actions';

export default function* clientSaga() {
	yield takeLatest(ClientActions.API_GET_CLIENTS_DATA, getClientsData);
	yield takeLatest(ClientActions.API_POST_CLIENT_DATA, postClientData);
	yield takeLatest(ClientActions.API_PUT_CLIENT_DATA, putClientData);
	yield takeLatest(ClientActions.API_GET_CLIENT_DATA, getClientData);
	yield takeLatest(ClientActions.API_EXCHANGE_ACCESS_TOKEN, exchangeAccessToken);
	yield takeLatest(ClientActions.API_DELETE_CLIENT_DATA, deleteClientData);
	yield takeLatest(ClientActions.API_GET_SCOPES_PAGE_CLIENTS_DATA, getScopesPageClientsData);
	yield takeLatest(ClientActions.API_GET_SCOPES_LINK_TO_CLIENT, getScopesLinkToClient);
	yield takeLatest(ClientActions.API_PUT_SCOPES_LINK_TO_CLIENT, putScopesLinkToClient);
	yield takeLatest(ClientActions.API_GET_SYSTEM_CONTEXTS, getSystemContexts);
	yield takeLatest(ClientActions.API_GET_SESSION_USER, getSessionUser);
}

function* getSystemContexts() {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makeGetRequest(systemContextsApi));
		yield put({ type: ClientActions.API_GET_SYSTEM_CONTEXTS_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

function* putScopesLinkToClient(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() =>
			makePutRequestWithQueryParams(clientsApi + '/' + action.payload.clientId + '/scopes', action.payload.scopes, {
				systemContextNum: action.payload?.systemContextNum
			})
		);
		yield put({ type: ClientActions.API_PUT_SCOPES_LINK_TO_CLIENT_SUCCESS, payload: response.data });
		yield put({ type: 'APP_SHOW_ALERT', payload: { type: 'success', message: clientMsgs.scopesLinkToClientSuccess } });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.scopesLinkToClientFailed, true);
	}
}

function* getScopesLinkToClient(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() =>
			makeGetRequestWithQueryParams(clientsApi + '/' + action.payload?.client_id + '/scopes', {
				systemContextNum: action.payload?.systemContextNum
			})
		);
		yield put({ type: ClientActions.API_GET_SCOPES_LINK_TO_CLIENT_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.getScopesToClientFailed, true);
	}
}

function* deleteClientData(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() =>
			makeDeleteRequestWithParams(clientsApi + '/' + action.payload.client_id, { systemContextNum: action.payload?.systemContextNum })
		);
		yield put({ type: ClientActions.API_DELETE_CLIENT_DATA_SUCCESS, payload: response.data });
		yield put({ type: 'APP_SHOW_ALERT', payload: { type: 'success', message: clientMsgs.deleteClientSuccess } });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

export function* checkRefreshToken() {
	let bearerToken = sessionStorage.getItem('access_token');
	if (bearerToken && Date.now() > sessionStorage.getItem('expires_at')) {
		try {
			const payload = {
				client_id: 'cas-vault-app',
				grant_type: 'refresh_token',
				refresh_token: sessionStorage.getItem('refresh_token')
			};
			const response = yield call(() => makePostRequest(tokenAPI, payload));
			yield put({ type: ClientActions.API_EXCHANGE_ACCESS_TOKEN_SUCCESS, payload: response.data });
		} catch (error) {
			yield* loadAndAlertMsg(error, clientMsgs.authFailed);
		}
	}
}

function* exchangeAccessToken(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makePostRequestWithFormData(tokenAPI, action.payload));
		yield put({ type: ClientActions.API_EXCHANGE_ACCESS_TOKEN_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.authFailed);
	}
}

function* getClientsData() {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makeGetRequest(clientsApi));

		yield put({ type: ClientActions.API_GET_CLIENTS_DATA_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

function* getScopesPageClientsData() {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makeGetRequest(clientsApi));
		yield put({ type: ClientActions.API_GET_SCOPES_PAGE_CLIENTS_DATA_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

function* getClientData(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() =>
			makeGetRequestWithQueryParams(clientsApi + '/' + action.payload?.client_id, { systemContextNum: action.payload?.systemContextNum })
		);
		yield put({ type: ClientActions.API_GET_CLIENT_DATA_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

function* postClientData(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makePostRequest(clientsApi, action.payload));
		yield put({ type: ClientActions.API_POST_CLIENT_DATA_SUCCESS, payload: response.data });
		yield put({ type: 'APP_SHOW_ALERT', payload: { type: 'success', message: clientMsgs.addClientSuccess } });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

function* putClientData(action) {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makePutRequest(clientsApi + '/' + action.payload.clientId, action.payload));
		yield put({ type: ClientActions.API_PUT_CLIENT_DATA_SUCCESS, payload: response.data });
		yield put({ type: 'APP_SHOW_ALERT', payload: { type: 'success', message: clientMsgs.updateClientSuccess } });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}

function* getSessionUser() {
	yield put({ type: ClientActions.FETCHING_DATA });
	try {
		const response = yield call(() => makeGetRequest(sessionUserApi));

		yield put({ type: ClientActions.API_GET_SESSION_USER_SUCCESS, payload: response.data });
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
	} catch (error) {
		yield put({ type: ClientActions.FETCHING_DATA_DONE });
		yield* loadAndAlertMsg(error, clientMsgs.unexpectedError);
	}
}
